import { attachPublishedVendor, type FetchPublishedVendor } from './attachPublishedVendor'
import {
  IEnum_Solution_Type,
  type IConsultancyBySlugQuery,
  type IConsultancyBySlugQueryVariables,
} from './generated/requests'

type BaseConsultancyBySlug = (
  variables: IConsultancyBySlugQueryVariables,
  requestHeaders?: any,
) => Promise<IConsultancyBySlugQuery>

function normalizeSlug(value: string | undefined): string {
  return value?.toLowerCase().trim() ?? ''
}

/**
 * Calls the generated consultancyBySlug request, then narrows to Consultancy-type solutions
 * and picks one by slug (case-insensitive) or by comma-separated oldSlug entries.
 * Replaces nested draft vendor with the published vendor for the same documentId.
 */
export async function consultancyBySlugCustom(
  baseConsultancyBySlug: BaseConsultancyBySlug,
  variables: IConsultancyBySlugQueryVariables,
  requestHeaders?: any,
  fetchPublishedVendor?: FetchPublishedVendor,
): Promise<IConsultancyBySlugQuery> {
  const response = await baseConsultancyBySlug(variables, requestHeaders)
  const solutions =
    response.solutions?.filter((s) => s?.type === IEnum_Solution_Type.Consultancy) ?? []

  const targetSlug = normalizeSlug(variables.slug)
  if (!targetSlug) {
    return { solutions: [] }
  }

  const exactMatch = solutions.find((s) => normalizeSlug(s?.slug ?? undefined) === targetSlug)
  const oldSlugMatch = solutions.find((s) => {
    const oldSlug = s?.oldSlug ?? ''
    const parts = oldSlug.split(',').map((part) => normalizeSlug(part))
    return parts.includes(targetSlug)
  })
  const match = exactMatch ?? oldSlugMatch
  if (!match) {
    return { solutions: [] }
  }

  if (!fetchPublishedVendor) {
    return { solutions: [match] }
  }

  const withPublishedVendor = await attachPublishedVendor(match, fetchPublishedVendor, requestHeaders)
  return { solutions: [withPublishedVendor] }
}
